import { Button, SkeletonShape } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { AnimatePresence, m } from 'motion/react';
import { useMatch } from '@/services/router/compat';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { useCopilotSessionContext } from '@/features/copilot/components/session-provider';
import { useShouldShowAgentBar } from '../../hooks/use-agent-fullscreen';
import { useSidebarActive } from '@/features/sidebar';
import { useCopilotSidebar } from '@/features/copilot/hooks/use-copilot-sidebar';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
// oxlint-disable-next-line local-rules/prefer-use-mount-effect -- syncs GlobalCSSVariable when isHidden changes
import { useEffect, useRef } from 'react';
import { Suggestions, useSuggestions, type ResolvedSuggestion } from '../suggestions';
import { AskAgentSparkleIcon } from '../ask-agent-sparkle-icon';
import * as styles from './index.css';

const ASSISTANT_FOOTER_HEIGHT = '45px';

const TRANSITION = { duration: 0.2, ease: [0.2, 0, 0.38, 0.9] } as const;

type AskAssistantButtonProps = { onClick: () => void };

function AskAssistantButton({ onClick }: AskAssistantButtonProps) {
  return (
    <GradientRing radius={4}>
      <Button variant="secondary" size="sm" className={gradientHoverTarget} onClick={onClick}>
        <AskAgentSparkleIcon />
        Ask the Agent
      </Button>
    </GradientRing>
  );
}

export function AssistantFooter() {
  const isCopilotSidebarOpen = useSidebarActive('copilot');
  const shouldShowAgentBar = useShouldShowAgentBar();
  const session = useCopilotSessionContext();
  const suggestionState = useSuggestions(session);
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const themeContext = useThemeContext();
  const isHomePage = useMatch('/workspaces/:workspaceId/home') !== null;

  const isHidden = isCopilotSidebarOpen || !shouldShowAgentBar || isHomePage;
  const { isVisible, suggestions, routeGroup, source } = suggestionState;
  const lastImpressionRef = useRef<string | null>(null);

  useEffect(() => {
    themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, isHidden ? '0px' : ASSISTANT_FOOTER_HEIGHT);

    return () => {
      themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, '0px');
    };
  }, [isHidden, themeContext]);

  useEffect(
    function trackSuggestionImpression() {
      // `isVisible` is already gated on a non-empty list by useSuggestions.
      if (isHidden || !isVisible) {
        return;
      }

      const labels = suggestions.map((suggestion) => suggestion.label);
      // Chips re-render on every path change; key the impression on what was
      // actually shown so a repaint of the same set isn't counted twice.
      const impressionKey = `${routeGroup}|${source}|${labels.join('|')}`;
      if (lastImpressionRef.current === impressionKey) {
        return;
      }
      lastImpressionRef.current = impressionKey;

      void track('aiAssistant', 'suggestionsDisplayed', {
        'Click Location': 'footer',
        'Route Group': routeGroup,
        'Suggestion Source': source,
        'Suggestion Labels': labels,
        Count: labels.length,
      });
    },
    [isHidden, isVisible, suggestions, routeGroup, source, track],
  );

  if (isHidden) {
    return null;
  }

  const handleSuggestionClick = async (message: string, index: number, suggestion: ResolvedSuggestion) => {
    try {
      openCopilot({ triggerSource: 'footer_suggestion' });
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'footer',
        'Conversation ID': session.id,
        'Suggestion Label': suggestion.label,
        'Suggestion Group': suggestion.group ?? 'none',
        'Route Group': routeGroup,
        'Suggestion Source': source,
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  const handleAskAssistant = () => {
    openCopilot({ triggerSource: 'footer' });
  };

  return (
    <div className={styles.footer}>
      <div className={styles.suggestionsContainer}>
        <AnimatePresence mode="popLayout">
          {isVisible
            ? suggestions.map((suggestion, index) => (
                <m.div
                  key={suggestion.label}
                  layout
                  initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  transition={{ ...TRANSITION, delay: index * 0.05 }}
                >
                  <Suggestions
                    suggestions={[suggestion]}
                    onSuggestionClick={(message) => handleSuggestionClick(message, index + 1, suggestion)}
                  />
                </m.div>
              ))
            : [140, 120, 160].map((width, index) => (
                <m.div
                  key={`skeleton-${index}`}
                  layout
                  initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  transition={{ ...TRANSITION, delay: index * 0.05 }}
                >
                  <SkeletonShape style={{ width, height: 29, borderRadius: themeVars.radius['4'] }} />
                </m.div>
              ))}
        </AnimatePresence>
      </div>
      <div className={styles.actionsContainer}>
        <AskAssistantButton onClick={handleAskAssistant} />
      </div>
    </div>
  );
}
